import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface ListingData {
  id: number;
  title: string;
  slug?: string;
  category_id: string;
  region_id: number | null;
  region_name?: string;
  category_name?: string;
  category_slug?: string;
  phone: string;
  email: string;
  website: string;
  description: string;
  image_url: string;
  services: string[];
  badge: string;
}

interface FormData {
  listing: ListingData;
  gallery_images: { id: number; url: string; caption: string; sort_order: number }[];
  categories: { id: string; name: string; slug: string }[];
  regions: { id: number; name: string }[];
}

export default function Dashboard() {
  const [token, setToken] = useState<string | null>(null);
  const [listingInfo, setListingInfo] = useState<{ id: number; title: string; email: string } | null>(null);
  const [data, setData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const t = localStorage.getItem('marinelink_owner_token');
    const l = localStorage.getItem('marinelink_owner_listing');
    if (!t || !l) {
      navigate('/signin');
      return;
    }
    setToken(t);
    setListingInfo(JSON.parse(l));
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    fetch('/.netlify/functions/auth-listing', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError('Failed to load listing data'))
      .finally(() => setLoading(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem('marinelink_owner_token');
    localStorage.removeItem('marinelink_owner_listing');
    navigate('/signin');
  };

  if (loading) {
    return <div className="max-w-[800px] mx-auto px-4 py-12 text-center text-text-muted">Loading your dashboard…</div>;
  }

  if (error) {
    return (
      <div className="max-w-[800px] mx-auto px-4 py-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 mb-4">{error}</div>
        <button onClick={logout} className="btn btn-ghost btn-sm">Sign out</button>
      </div>
    );
  }

  if (!data || !listingInfo) return null;

  const { listing, gallery_images } = data;

  return (
    <div className="max-w-[900px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-1">Listing Dashboard</h1>
          <p className="text-text-secondary text-sm">{listingInfo.email}</p>
        </div>
        <button onClick={logout} className="btn btn-ghost btn-sm">Sign out</button>
      </div>

      {/* Listing preview card */}
      <div className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg p-5 mb-6">
        <div className="flex gap-4">
          {listing.image_url && (
            <img src={listing.image_url} alt={listing.title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-bold truncate">{listing.title}</h2>
            <p className="text-text-secondary text-sm">
              {listing.category_name || 'Uncategorised'} · {listing.region_name || 'No region'}
            </p>
            <p className="text-text-muted text-xs mt-1">
              {gallery_images.length} gallery image{gallery_images.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Action cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Link to="/edit-listing" className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg p-5 hover:border-accent transition-colors group">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold">Edit Listing Details</h3>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted group-hover:text-accent transition-colors">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <p className="text-text-secondary text-sm">Update your business name, description, contact details, website, services, and category.</p>
        </Link>

        <Link to="/manage-gallery" className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg p-5 hover:border-accent transition-colors group">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold">Manage Photo Gallery</h3>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted group-hover:text-accent transition-colors">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <p className="text-text-secondary text-sm">Add, remove, and reorder photos. Captions and sort order for your gallery images.</p>
        </Link>
      </div>

      {/* Quick stats */}
      <div className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg p-5">
        <h3 className="font-semibold mb-3 text-sm">Listing Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-text-muted text-xs mb-1">Phone</p>
            <p className="font-medium">{listing.phone || 'Not set'}</p>
          </div>
          <div>
            <p className="text-text-muted text-xs mb-1">Email</p>
            <p className="font-medium truncate">{listing.email || 'Not set'}</p>
          </div>
          <div>
            <p className="text-text-muted text-xs mb-1">Website</p>
            <p className="font-medium truncate">{listing.website || 'Not set'}</p>
          </div>
          <div>
            <p className="text-text-muted text-xs mb-1">Services</p>
            <p className="font-medium">{listing.services?.length || 0} listed</p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <a href={`https://marinelink.co.nz/listing/${listing.category_slug || 'all'}/${listing.slug || listing.id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-accent hover:underline">
          View your live listing ↗
        </a>
      </div>
    </div>
  );
}
